/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';
import { IconComponent, IconVariants } from '../icon/icon.component';
import { BadgeIconName } from '../icon/icons';

export const badgeVariants = cva('flex gap-1 items-center rounded-full text-sm border', {
  variants: {
    variant: {
      primary: 'border-transparent text-whitetext',
      outline: 'bg-transparent',
    },
    size: {
      small: 'px-2 py-0.5',
      medium: 'px-5 py-1',
    },
    color: {
      primary: '',
      success: '',
      warning: '',
      error: '',
      disabled: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'small',
    color: 'primary',
  },
  compoundVariants: [
    { variant: 'primary', color: 'primary', class: 'bg-primary' },
    { variant: 'primary', color: 'success', class: 'bg-success' },
    { variant: 'primary', color: 'warning', class: 'bg-warning-dark ' },
    { variant: 'primary', color: 'error', class: 'bg-error' },
    { variant: 'primary', color: 'disabled', class: 'bg-disabled-button' },
    { variant: 'outline', color: 'primary', class: 'border-primary text-primary' },
    { variant: 'outline', color: 'success', class: 'border-success text-success' },
    { variant: 'outline', color: 'warning', class: 'border-warning-dark text-warning-dark' },
    { variant: 'outline', color: 'error', class: 'border-error text-error' },
    { variant: 'outline', color: 'disabled', class: 'border-disabled-button text-disabled-button' },
  ],
});

export type BadgeVariants = VariantProps<typeof badgeVariants>;

export interface BadgeConfig {
  text: string;
  color: BadgeVariants['color'];
}

@Component({
  selector: 'ods-badge',
  imports: [CommonModule, IconComponent],
  template: `<p [ngClass]="badgeVariants({ variant, size, color })" data-test-id="badge-container">
    @if (icon) {
      <ods-icon [name]="icon" [config]="{ fill: iconFill, size: 'small' }" />
    }
    {{ name }}
  </p>`,
  host: { class: 'block w-fit' },
})
export class BadgeComponent {
  @Input() name: string;
  @Input() icon?: BadgeIconName;
  @Input() variant: BadgeVariants['variant'];
  @Input() size: BadgeVariants['size'];
  @Input() color: BadgeVariants['color'];

  public readonly badgeVariants = badgeVariants;

  get iconFill(): IconVariants['fill'] {
    if (this.variant !== 'outline') {
      return 'whitetext';
    }
    return this.color === 'warning' ? 'warning-dark' : (this.color ?? 'primary');
  }
}
